import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	createSupportTicket,
	getSupportTicket,
	getSupportTickets,
	updateSupportTicket,
	type CreateSupportTicketPayload,
	type UpdateSupportTicketPayload,
} from '@/services/supportTickets';
import type { FilterType } from '@/types/table';
import type { SupportTicketDetailsResponse } from '@/types/supportTicket';

export const useGetSupportTickets = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'supportTickets',
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		],
		queryFn: () =>
			getSupportTickets(
				page,
				limit,
				columnsFilters,
				search,
				status as string,
				globalFilters as string,
				sort as string,
			),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetSupportTicket = (id: string | null, enabled = true) => {
	return useQuery<SupportTicketDetailsResponse>({
		queryKey: ['supportTicket', id],
		queryFn: () => getSupportTicket(id as string),
		enabled: enabled && !!id,
		keepPreviousData: false,
	});
};

export const useCreateSupportTicket = (onSuccess?: () => void, onError?: (error: any) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (ticketData: CreateSupportTicketPayload) => createSupportTicket(ticketData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
			// No toast here - form handles success/error display
			onSuccess?.();
		},
		onError: (error) => {
			// No toast here - form handles success/error display
			onError?.(error);
		},
	});
};

export const useUpdateSupportTicket = (onSuccess?: () => void, onError?: (error: any) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, ticketData }: { id: string; ticketData: UpdateSupportTicketPayload }) =>
			updateSupportTicket(id, ticketData),
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
			queryClient.invalidateQueries({ queryKey: ['supportTicket', variables.id] });
			// No toast here - form handles success/error display
			onSuccess?.();
		},
		onError: (error) => {
			// No toast here - form handles success/error display
			onError?.(error);
		},
	});
};

